import { useState, type ReactNode } from "react";
import { Copy, Check, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// #186: Generischer Bootstrap-Anweisungs-Dialog. Zeigt nach Create einer
// Resource (Channel/Node/…) die einmaligen Secrets als kopierbare Blöcke PLUS
// einen konkreten Run-Command + optionale Hints — damit der Operator weiß was
// er jetzt mit dem Bundle macht. hideCloseButton: kein X, expliziter Button.

export interface ProvisioningBlock {
  label: string;
  /** Mehrzeiliger Inhalt (z.B. ENV-Block). Wird monospace + kopierbar dargestellt. */
  value: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Kopierbare Secret-/Config-Blöcke (z.B. ENV-Bundle). */
  blocks: ProvisioningBlock[];
  /** Konkreter Shell-Command zum Starten (z.B. docker run …). */
  runCommand?: string;
  /** Zusätzliche Klartext-Hinweise (z.B. "QR-Code via docker logs -f"). */
  hints?: string[];
}

function CopyBox({ label, value, testid }: { label: string; value: string; testid: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div className="space-y-1">
      <label className="text-xs text-subtext0">{label}</label>
      <div className="flex gap-2 items-start">
        <pre
          className="flex-1 rounded-md border border-border bg-base px-3 py-2 font-mono text-xs text-text whitespace-pre-wrap break-all max-h-48 overflow-auto"
          data-testid={`${testid}-value`}
        >
          {value}
        </pre>
        <Button variant="outline" size="sm" onClick={copy} data-testid={`${testid}-copy`} aria-label={`${label} kopieren`}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export function ProvisioningInstructionsDialog({
  open,
  onOpenChange,
  title,
  description,
  blocks,
  runCommand,
  hints,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={(o) => o && onOpenChange(o)}>
      <DialogContent hideCloseButton className="max-w-2xl" data-testid="provisioning-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md bg-yellow/10 border border-yellow/30 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-yellow mt-0.5 shrink-0" />
          <div className="text-yellow">
            Einmalige Anzeige. Nach dem Schließen können die Credentials nicht mehr abgerufen werden — JETZT kopieren.
          </div>
        </div>

        <div className="space-y-3 max-h-[55vh] overflow-auto">
          {blocks.map((b, i) => (
            <CopyBox key={b.label} label={b.label} value={b.value} testid={`provisioning-block-${i}`} />
          ))}

          {runCommand && (
            <CopyBox label="Container starten" value={runCommand} testid="provisioning-run" />
          )}

          {hints && hints.length > 0 && (
            <ul className="text-xs text-overlay0 list-disc pl-4 space-y-0.5">
              {hints.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} data-testid="provisioning-close">
            Kopiert, Dialog schliessen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
